/** @module httpUtils */

export const httpRequestTimeout = 5000;

const parsers = {
  text: response => response.text(),
  json: response => response.json(),
  document: response => response.text().then(text => new DOMParser().parseFromString(text, 'application/xml')),
};

const withQuery = (url, query) => {
  const search = query ? new URLSearchParams(query).toString() : '';

  if (!search) {
    return url;
  }

  return `${url}${url.includes('?') ? '&' : '?'}${search}`;
};

const makeBody = (body, isForm) => {
  if (body === undefined) {
    return {};
  }

  if (isForm) {
    return { body: new URLSearchParams(body).toString(), contentType: 'application/x-www-form-urlencoded' };
  }

  return typeof body === 'string' ? { body } : { body: JSON.stringify(body), contentType: 'application/json' };
};

// NOTE: some of the target browsers have no AbortController, the request is not cancelled there, only abandoned
const fetchWithTimeout = (url, options, timeout) => {
  if (!timeout) {
    return fetch(url, options);
  }

  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  let timer = null;

  const timeoutError = new Promise((_, reject) => {
    timer = setTimeout(() => {
      if (controller) {
        controller.abort();
      }

      reject(new Error(`Request timeout of ${timeout}ms exceeded: ${url}`));
    }, timeout);
  });

  return Promise.race([
    fetch(url, controller ? { ...options, signal: controller.signal } : options),
    timeoutError,
  ]).then(
    response => {
      clearTimeout(timer);

      return response;
    },
    error => {
      clearTimeout(timer);

      throw error;
    },
  );
};

/**
 * Performs an HTTP request
 *
 * @param {string} url
 * @param {Object} [options]
 * @param {string} [options.method='GET']
 * @param {Object} [options.query] query parameters, appended to the URL
 * @param {Object} [options.headers]
 * @param {Object|string} [options.body] an object is sent as JSON, unless `form` is set
 * @param {boolean} [options.form] sends the body as a URL-encoded form
 * @param {string} [options.responseType='text'] `text`, `json` or `document` (an XML document)
 * @param {number} [options.timeout] milliseconds
 * @returns {Promise} resolves with the parsed response, rejects on a network error, a timeout or a non 2xx status
 */
export const request = (
  url,
  { method = 'GET', query, headers = {}, body, form = false, responseType = 'text', timeout } = {},
) => {
  const { body: payload, contentType } = makeBody(body, form);
  const options = {
    method,
    headers: contentType ? { 'Content-Type': contentType, ...headers } : headers,
    ...(payload === undefined ? {} : { body: payload }),
  };

  return fetchWithTimeout(withQuery(url, query), options, timeout).then(response => {
    if (!response.ok) {
      const error = new Error(`HTTP ${response.status} on ${url}`);
      error.status = response.status;

      throw error;
    }

    return parsers[responseType](response);
  });
};

/**
 * Fires a request without caring about its result, i.e. a tracking pixel
 *
 * @param {string} url
 * @returns {Promise} always resolves
 */
export const ping = url => fetch(url, { mode: 'no-cors' }).catch(() => {});
